import React, { useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import 'dayjs/locale/id'

import { Text } from '@wartek-id/text-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Select } from '@wartek-id/select'

import {
  DatePicker,
  type DatePickerProps,
} from 'app/KinerjaShared/components/DatePicker'
import { Card, CardBody } from 'app/KinerjaShared/components/Card'
import { formatDate } from 'utils/datetime'

import type { RepeatingDateType } from 'types/kinerja-harian'
import type { Dayjs } from 'dayjs'

const REPEATING_DATE_MAPPING: Record<RepeatingDateType, string> = {
  'non-repeating': 'Tidak diulang',
  weekly: 'Setiap minggu',
  workday: 'Setiap Hari Kerja - Senin sampai Jumat',
  'workday-with-saturday': 'Setiap Hari Kerja - Senin sampai Sabtu',
}

type WeekGroup = { weekNum: number; dates: Dayjs[] }
type MonthGroup = { label: string; weeks: WeekGroup[]; totalDates: number }

function getSemesterEnd(date: Dayjs): Dayjs {
  const month = date.month() + 1
  if (month <= 6) {
    return dayjs(`${date.year()}-06-30`)
  }
  return dayjs(`${date.year()}-12-31`)
}

function getWeekNumInAMonth(date: Dayjs): number {
  const firstDayOfMonth = date.startOf('month')
  const firstDayOfWeek = firstDayOfMonth.day()

  if (firstDayOfWeek === 0) {
    return Math.ceil(date.date() / 7)
  } else {
    const offset = 7 - firstDayOfWeek
    if (date.date() <= offset) {
      return 1
    }
    return Math.ceil((date.date() - offset) / 7) + 1
  }
}

function generateAndGroupDates(
  startDate: string,
  repeatingDateType: RepeatingDateType,
  holidayDates: Set<string> = new Set()
): MonthGroup[] {
  const start = dayjs(startDate).locale('id') as Dayjs
  const end = getSemesterEnd(start)
  const monthMap = new Map<string, Map<number, Dayjs[]>>()

  let current = start

  while (current.valueOf() <= end.valueOf()) {
    const day = current.day() // 0=Sun, 1=Mon, ..., 6=Sat

    let include = false
    if (repeatingDateType === 'weekly') {
      include = day === start.day()
    } else if (repeatingDateType === 'workday') {
      include = day >= 1 && day <= 5
    } else if (repeatingDateType === 'workday-with-saturday') {
      include = day >= 1 && day <= 6
    }

    if (holidayDates.size > 0) {
      include = include && !holidayDates.has(current.format('YYYY-MM-DD'))
    }

    if (include) {
      const monthKey = current.format('MMMM YYYY').toUpperCase()
      const weekNum = getWeekNumInAMonth(current)

      if (!monthMap.has(monthKey)) {
        monthMap.set(monthKey, new Map())
      }

      const weekMap = monthMap.get(monthKey)!

      if (!weekMap.has(weekNum)) {
        weekMap.set(weekNum, [])
      }

      weekMap.get(weekNum)!.push(current)
    }

    current = current.add(1, 'day')
  }

  return Array.from(monthMap.entries()).map(([label, weekMap]) => {
    const weeks: WeekGroup[] = Array.from(weekMap.entries())
      .sort(([a], [b]) => a - b)
      .map(([weekNum, dates]) => ({ weekNum, dates }))
    return {
      label,
      weeks,
      totalDates: weeks.reduce((s, w) => s + w.dates.length, 0),
    }
  })
}

type DatesSelectorProps = DatePickerProps & {
  startDate: string | null
  onChangeStartDate: (date: string | null) => void
  repeatingDateType: RepeatingDateType
  onChangeRepeatingDateType: (type: RepeatingDateType) => void
  onChangeEntryDates?: (dates: string[]) => void
  holidayDates?: Set<string>
  disabled?: boolean
}

export function DatesSelector({
  startDate,
  onChangeStartDate,
  repeatingDateType,
  onChangeRepeatingDateType,
  onChangeEntryDates,
  holidayDates = new Set(),
  disabled = false,
  ...restProps
}: DatesSelectorProps) {
  const monthGroups = useMemo(() => {
    if (!startDate || repeatingDateType === 'non-repeating') return []
    return generateAndGroupDates(startDate, repeatingDateType, holidayDates)
  }, [startDate, repeatingDateType, holidayDates])

  const totalDates = useMemo(
    () => monthGroups.reduce((s, m) => s + m.totalDates, 0),
    [monthGroups]
  )

  const [openMonths, setOpenMonths] = useState<Set<string>>(new Set())

  const toggleMonth = (label: string) => {
    setOpenMonths((prev) => {
      const next = new Set(prev)

      if (next.has(label)) {
        next.delete(label)
      } else {
        next.add(label)
      }

      return next
    })
  }

  useEffect(() => {
    if (repeatingDateType === 'non-repeating') {
      onChangeEntryDates?.(startDate ? [startDate] : [])
    } else {
      const allDates = monthGroups.flatMap((m) =>
        m.weeks.flatMap((w) => w.dates)
      )
      onChangeEntryDates?.(allDates.map((d) => d.format('YYYY-MM-DD')))
    }
  }, [startDate, repeatingDateType, monthGroups, onChangeEntryDates])

  return (
    <Card withBorder>
      <CardBody className="p-4">
        {!disabled && (
          <>
            <header className="flex gap-2 mb-3">
              <Icon>calendar_today_outline</Icon>
              <div>
                <Text variant="heading-md" as="h2">
                  Tanggal
                </Text>
                <Text className="mt-1">
                  Pilih satu tanggal lalu pilih ulangi jika ingin mencatat untuk
                  banyak tanggal sekaligus.
                </Text>
              </div>
            </header>

            <section className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              <div>
                <Text variant="body-sm" id="entry-date" className="block mb-1">
                  Pilih tanggal
                </Text>
                <DatePicker
                  aria-labelledby="entry-date"
                  useCalendar
                  value={startDate}
                  onChange={onChangeStartDate}
                  isDateUnavailable={(date) => {
                    if (repeatingDateType === 'non-repeating') {
                      return false
                    }

                    const localDate = date.toDate('Asia/Jakarta')

                    return (
                      localDate.getDay() === 0 ||
                      holidayDates.has(formatDate(localDate, 'yyyy-MM-dd'))
                    )
                  }}
                  {...restProps}
                />
              </div>
              <div>
                <Text
                  variant="body-sm"
                  as="label"
                  htmlFor="repeat"
                  className="block mb-1"
                >
                  Ulangi
                </Text>
                <Select
                  id="repeat"
                  className="!pt-3.5 !pb-4"
                  value={repeatingDateType}
                  onChange={(e) =>
                    onChangeRepeatingDateType(
                      e.target.value as RepeatingDateType
                    )
                  }
                >
                  {Object.entries(REPEATING_DATE_MAPPING).map(
                    ([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    )
                  )}
                </Select>
              </div>
            </section>
          </>
        )}

        {(monthGroups.length > 0 ||
          (repeatingDateType === 'non-repeating' && startDate)) && (
          <Card className="border border-blue-70 rounded-lg overflow-hidden">
            <CardBody className="p-4 !bg-blue-10">
              <Text variant="body" className="block mb-3">
                {disabled && startDate ? (
                  'Tanggal yang akan diubah'
                ) : (
                  <>
                    Tanggal yang akan dicatat (
                    {repeatingDateType === 'non-repeating' ? 1 : totalDates}{' '}
                    tanggal)
                  </>
                )}
              </Text>

              {repeatingDateType === 'non-repeating' ? (
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white rounded border border-gray-200 w-full">
                  <Icon fontSize="sm" color="informational">
                    calendar_today_outline
                  </Icon>
                  <Text variant="body">
                    {dayjs(startDate).locale('id').format('dddd, D MMMM YYYY')}
                  </Text>
                </div>
              ) : (
                <div className="space-y-2">
                  {monthGroups.map((monthGroup) => (
                    <div key={monthGroup.label}>
                      <button
                        type="button"
                        className="flex items-center gap-2 w-full mb-2 text-left"
                        onClick={() => toggleMonth(monthGroup.label)}
                      >
                        <Text
                          variant="body-sm-bold"
                          color="informational"
                          as="span"
                          className="whitespace-nowrap"
                        >
                          {monthGroup.label} ({monthGroup.totalDates} TANGGAL)
                        </Text>
                        <div className="flex-1 h-px bg-blue-70" />
                        <Icon color="informational">
                          {openMonths.has(monthGroup.label)
                            ? 'expand_less'
                            : 'expand_more'}
                        </Icon>
                      </button>

                      {openMonths.has(monthGroup.label) && (
                        <div className="flex flex-wrap gap-2 mb-2">
                          {monthGroup.weeks
                            .flatMap((w) => w.dates)
                            .map((date) => (
                              <div
                                key={date.format('YYYY-MM-DD')}
                                className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white rounded border border-gray-200"
                              >
                                <Icon fontSize="sm" color="informational">
                                  calendar_today_outline
                                </Icon>
                                <Text variant="body">
                                  {date.format('dddd, D MMMM')}
                                </Text>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>
        )}
      </CardBody>
    </Card>
  )
}
